import ThirdPartyLoader from 'components/global/ThirdPartyLoader';
import { on, trigger } from 'toolbox/event';
import { deepMerge } from 'toolbox/deepMerge';

/**
 * This is a description of the ReCaptchaV3 constructor function.
 * @class
 * @classdesc This is a description of the ReCaptchaV3 class.
 * @extends Component
 */
export default class ReCaptchaV3 extends ThirdPartyLoader {
    /**
     * Constructor of the class that mainly merge the options of the components
     * @param {HTMLElement} element HTMLElement of the component
     * @param {Object} options options that belongs to the component
     */
    constructor(element, options = {}) {
        super(element, deepMerge({
            scriptUrl: '//www.google.com/recaptcha/api.js',
            sitekey: null,
        }, options));
    }

    /**
     * Init the different state of the component
     * It helps to avoid heavy DOM manipulation
     */
    initState() {
        this.state.scriptLoaded = false;
    }

    /**
     * All selectors must be cached. Never cache elements that are out of the component scope
     */
    initCache() {
        this.classes = {
            captcha: 'g-recaptcha',
            captchaResponse: 'g-recaptcha-response',
        };
    }

    /**
     * Called after component initialization
     */
    afterInit() {
        super.loadScript();
    }

    /**
     * Should contain only event listeners and nothing else
     * All the event handlers should be into a separated function. No usage of anonyous function
     */
    bindEvents() {
        on('captcha.execute', this.element, this.executeCaptcha.bind(this));
    }

    /**
     * Get path of recaptcha
     */
    get path() {
        const { scriptUrl, sitekey } = this.options;
        return `${scriptUrl}?render=${sitekey}`;
    }

    /**
     * On script load handler, called when ReCaptchaV3 is loaded
     */
    onScriptLoaded() {
        super.onScriptLoaded();
        this.state.scriptLoaded = true;
        if (grecaptcha !== undefined) {
            grecaptcha.ready(() => {
                this.element.classList.add(this.classes.captcha);
            });
        }
    }

    /**
     * On ReCaptchaV3 script loading failed handler
     *
     * @param {Object} error - Event object
     */
    onScriptLoadFailed(error) {
        super.onScriptLoadFailed();
        console.error(error.message);
    }

    /**
     * Call execute function for Google ReCaptchaV3
     * @param {Event} e event object
     */
    executeCaptcha(e) {
        const { form, onExecuteSuccess } = e.detail;
        const { sitekey } = this.options;

        grecaptcha.execute(sitekey, { action: 'submit' }).then((token) => {
            let captchaResponse = form.querySelector(`input[name="${this.classes.captchaResponse}"]`);

            if (!captchaResponse) {
                captchaResponse = document.createElement('input');
                captchaResponse.setAttribute('type', 'hidden');
                captchaResponse.setAttribute('name', this.classes.captchaResponse);
                captchaResponse.setAttribute('value', '');

                form.appendChild(captchaResponse);
            }

            captchaResponse.setAttribute('value', token);

            trigger('form.captcha.success', this.element, { bubbles: true });
            if (typeof onExecuteSuccess === 'function') {
                onExecuteSuccess(token);
            }
        });
    }
}
